<script setup>
import { computed, inject, onBeforeMount, ref, watch, onMounted } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import debounce from 'debounce'
import { useAnalyzer } from '#/analyzer/useAnalyzer'
import { useToast } from 'vue-toast-notification'
import { useLoader } from '#/general/useLoader'
import TooltipContainer from '#/general/TooltipContainer'
import { useRouter } from 'vue-router'
import { useGA } from '#/useGA'
import { useGlobalStore } from 'src/composables/useGlobalStore'
import Popper from 'vue3-popper'
import { apolloClient } from 'boot/apollo'

const { event } = useGA()
const { showLoader, hideLoader } = useLoader()
const { isLoggedIn } = useGlobalStore()
const $toast = useToast({ position: 'bottom' })
const props = defineProps({
  btnText: { type: String, default: 'Jetzt prüfen!' },
  btnTextMobile: { type: String, default: 'Jetzt prüfen!' },
  inputColor: { type: String, default: 'white' },
  inputBg: { type: String, default: 'rgba(0, 123, 255, 0.15)' },
  padding: { type: String, default: '14px 25px' },
  paddingMobile: { type: String, default: '14px 5px' },
  maxWidth: { type: String, default: '700px' },
  btnWidth: { type: String, default: '253px' },
  btnWidthMobile: { type: String, default: '125px' },
  autofocus: { type: Boolean, default: false }
})
const { screen, isBrowser } = inject('Shared')
const router = useRouter()
const {
  searchText,
  selectedSearchResult,
  showPopup,
  searchResults,
  findPotentialEnabled,
  searchAddressesEnabled,
  invalidPotentialBuildingCategory,
  invalidPotentialBuildingClass,
  showInvalidPotentialPopup,
  selectedPotential
} = useAnalyzer()

let searchAddresses = async () => {
  try {
    if (searchText.value && searchAddressesEnabled.value) {
      const res = await apolloClient.query({
        query: gql`
        query searchPotentialAddress ($searchText: String!) {
          searchPotentialAddress (searchText: $searchText) {
            address,
            id
          }
        }
      `,
        variables: { searchText: searchText.value }
      })
      showPopup.value = true
      if (res.data) {
        searchResults.value = JSON.parse(JSON.stringify(res?.data?.searchPotentialAddress))
      }
    }
  } catch (e) {
    console.log('Search error: ', JSON.parse(JSON.stringify(e)))
  }
}
searchAddresses = debounce(searchAddresses, 100)
watch(searchText, searchAddresses, { immediate: true })

const { onError: onFoundPotentialError, onResult: onFoundPotential, loading: findPotentialLoading } = useQuery(
  gql`
    query findPotentialByAddress ($addressId: String!, $address: String!) {
      findPotentialByAddress (addressId: $addressId, address: $address) {
        searchedAddress,
        queryId,
        success,
        error,
        potential {
          _id,
          lat,
          long,
          hasLead,
          yearOfConstructionEndMax,
          city {
            name
            buildingRegulationsPage
          }
          parcel {
            area,
            parcelNr
            builtUpArea
            maxVolume
            currentVolume
            publicTransportQuality
            renovationPressure
            roofStats {
              flatRoofShare
            }
            geometryCoordinates {
              lat
              lon
            }
          }
          buildingZones {
            squareMeters
            buildingZone {
              shortName
              zoneType
            }
          }
          buildings {
            address,
            town,
            buildingCategory
          }
          analysis {
            score
            potentialVolume
            isBuildingZoneBased
          }
          oerebLink
        }
      }
    }
  `,
  () => { return { addressId: selectedSearchResult.value.addressId, address: selectedSearchResult.value.address } },
  () => { return { enabled: !!(selectedSearchResult.value.address && findPotentialEnabled.value === true), metadata: false, debounce: 300 } }
)
onFoundPotentialError(findPotentialError => {
  console.log('FIND POTENTIAL ERROR', JSON.parse(JSON.stringify(findPotentialError)))
  $toast.open({ type: 'info', message: 'Something went wrong while analyzing this address, please contact us at hallo@raumpioniere.ch to sort this out quickly.', duration: 5000 })
  if (findPotentialError.message) {
    const serverMessage = JSON.parse(findPotentialError.message)
    if (serverMessage.reason === 'INVALID_SEP_POTENTIAL') {
      showInvalidPotentialPopup.value = true
      invalidPotentialBuildingClass.value = serverMessage.buildingClass
      invalidPotentialBuildingCategory.value = serverMessage.buildingCategory
    }
  }
})
onFoundPotential(res => {
  try {
    res = JSON.parse(JSON.stringify(res))
    showPopup.value = false
    if (res.data) {
      if (res.data && res.data.findPotentialByAddress) {
        if (res.data.findPotentialByAddress.success) {
          navigateToAnalyzer()
          selectedPotential.value = { ...res.data.findPotentialByAddress.potential, searchedAddress: res.data.findPotentialByAddress.searchedAddress, queryId: res.data.findPotentialByAddress.queryId }
        } else {
          console.log('E', res.data)
          const error = JSON.parse(res.data.findPotentialByAddress.error)
          console.log('ERROR', error)
          if (error.reason === 'INVALID_SEP_POTENTIAL') {
            showInvalidPotentialPopup.value = true
            invalidPotentialBuildingClass.value = error.buildingClass
            invalidPotentialBuildingCategory.value = error.buildingCategory
          }
        }
      }
    }
  } catch (e) {
    console.log(e)
    console.log(JSON.parse(JSON.stringify(e)))
    searchText.value = null
    $toast.open({ type: 'info', message: 'Something went wrong while analyzing this address, please contact us at hallo@raumpioniere.ch to sort this out quickly.', duration: 5000 })
  }
})

const timer = ref()
const timer2 = ref()
watch(findPotentialLoading, v => {
  if (v) {
    showLoader({
      message: 'Sämtliche Daten für die Potenzialanalyse werden gesammelt.',
      type: 'IOS'
    })
    timer.value = setTimeout(() => {
      if (findPotentialLoading.value) {
        showLoader({
          message: 'Die Berechnungen laufen ...',
          type: 'HOURGLASS'
        })
        timer2.value = setTimeout(() => {
          if (findPotentialLoading.value) {
            showLoader({
              message: '... gleich sind wir soweit. Etwas Geduld.',
              type: 'HOURGLASS'
            })
          }
        }, 4000)
      }
    }, 4000)
  } else {
    hideLoader()
    clearTimeout(timer.value)
    clearTimeout(timer2.value)
    timer.value = void 0
    timer2.value = void 0
  }
}, { immediate: true })

onBeforeMount(() => {
  clearTimeout(timer.value)
  clearTimeout(timer2.value)
  timer.value = void 0
  timer2.value = void 0
})

watch(searchText, value => {
  if (!value) {
    showPopup.value = false
    searchResults.value = []
  } else {
    searchAddressesEnabled.value = true
    const sameRes = searchResults.value.find(e => e.address === value)
    if (sameRes) {
      resultClicked(sameRes)
    } else {
      selectedSearchResult.value = { address: null, addressId: null }
    }
  }
})

function resultClicked (result) {
  showPopup.value = false
  searchAddressesEnabled.value = false
  findPotentialEnabled.value = false
  selectedSearchResult.value = { addressId: result.id, address: result.address }
  searchText.value = result.address
}

const navigateToAnalyzer = () => {
  if (isLoggedIn.value) {
    router.push('/b2b/analyzer')
  } else {
    router.push('/b2c/analyzer')
  }
  event('main_user_flow', { event_category: 'main_user_flow', event_label: 'Analysis started' })
}

const contentWidth = computed(() => {
  if (screen.width > 1100) {
    return 'calc(100% - 40px)'
  } else if (screen.width > 600) {
    return '699px'
  }
  return 'calc(100vw - 40px)'
})

function closePopup () {
  showPopup.value = false
}
const isMounted = ref(false)
onMounted(() => {
  isMounted.value = true
})
</script>

<template lang="pug">
.b2c-search-box--container(v-click-away="closePopup")
  .b2c-search--input-container(@click="showPopup = true")
    .b2c-search--input-caret
    popper(
      :show="showPopup"
      @click="showPopup = true"
      placement="bottom"
      :offset-skid="(screen.width < 1100) ? undefined : '40'"
      :offset-distance="(screen.width < 1100) ? '35' : undefined"
      :interactive="true"
    ).input-menu--container
      input.b2c-search--input(:placeholder="screen.gt.xs ? 'Ihre Adresse eingeben' : 'Adresse eingeben'" v-model="searchText" :autofocus="autofocus")
      template(#content)
        .b2c-search-results--container(v-if="searchResults && searchResults.length > 0")
          .b2c-search-result(v-for="(result, index) in searchResults.slice(0, 3)" :key="index" @click="resultClicked(result)") {{ result.address }}
  popper(:interactive="false" hover v-if="isMounted").btn-menu--container
    .b2c-search--btn(@click="findPotentialEnabled = true") {{ screen.gt.sm ? btnText : btnTextMobile }}
    template(#content)
      tooltip-container(body="Bitte suchen sie nach einer Adresse und wählen diese aus der Liste aus." v-if="!selectedSearchResult || !selectedSearchResult.address || !searchText")
</template>

<style lang="sass">
.popper
  @media screen and (min-width: 600px)
    transform: translate(10px, 70px) !important
  @media screen and (max-width: 600px)
    transform: translate3d(-20px, 81px, 0px) !important
.input-menu--container
  @media screen and (max-width: 1024px)
    width: 100% !important
.btn-menu--container, .input-menu--container
  margin: 0px !important
  border: 0px !important
  div:first-child
    height: 100%
.b2c-search-results--container
  border: 2px solid #007BFF
  border-radius: 10px
  padding: 14px 25px
  @media screen and (min-width: 1100px)
    width: 432px
    background: #FFFFFF
  @media screen and (max-width: 1100px) and (min-width: 600px)
    width: 432px
    background: rgba(255, 255, 255, 0.9)
  @media screen and (max-width: 600px)
    margin-left: 20px
    width: calc(100vw - 60px)
    background: rgba(255, 255, 255, 0.9)
  .b2c-search-result
    font-weight: 500
    font-size: 16px
    line-height: 27px
    color: #626C85
    cursor: pointer
    transition: all 0.3s ease-in-out
    &:hover
      color: #007BFF !important
.b2c-search-box--container
  border-radius: 10px
  display: flex
  flex-direction: row
  @media screen and (min-width: 1100px)
    height: 88px
  @media screen and (max-width: 1100px)
    max-width: 100%
    height: 73px
.b2c-search--input-container
  border-bottom: 2px solid #007BFF
  border-left: 2px solid #007BFF
  border-top: 2px solid #007BFF
  height: 100%
  cursor: pointer
  border-radius: 10px 0px 0px 10px
  background: v-bind(inputBg)
  display: flex
  flex-direction: row
  align-items: center
  position: relative
  @media screen and (min-width: 1100px)
    width: 459px
    padding: v-bind(padding)
  @media screen and (max-width: 1100px) and (min-width: 600px)
    padding: v-bind(paddingMobile)
    width: 459px
  @media screen and (max-width: 1100px)
    padding: v-bind(paddingMobile)
  .b2c-search--input
    caret-color: #007BFF
    background: transparent
    border-radius: 10px 0px 0px 10px
    height: 100%
    width: 100%
    outline: none
    border: none
    font-weight: 500
    line-height: 30px
    color: v-bind(inputColor) !important
    @media screen and (min-width: 1100px)
      font-size: 24px
    @media screen and (max-width: 1100px)
      font-size: 16px
      margin-left: 7px
    &::placeholder
      font-weight: 500
      line-height: 30px
      color: v-bind(inputColor) !important
      @media screen and (min-width: 1100px)
        font-size: 24px
      @media screen and (max-width: 1100px)
        font-size: 16px
.b2c-search--btn
  border-bottom: 2px solid #007BFF
  border-right: 2px solid #007BFF
  border-top: 2px solid #007BFF
  height: 100%
  background: linear-gradient(180deg, #007BFF 0%, #1F47CD 100%)
  cursor: pointer
  display: flex
  justify-content: center
  align-items: center
  font-weight: 600
  line-height: 30px
  color: white
  border-radius: 0px 7px 7px 0px
  @media screen and (min-width: 1100px)
    min-width: v-bind(btnWidth)
    padding: 26px 30px
    font-size: 24px
  @media screen and (max-width: 1100px)
    width: v-bind(btnWidthMobile)
    font-size: 16px

</style>
